import { FC, useState } from 'react';
import { NewUserStorageDataIF } from '@/types/common';
import { FILE_VALIDATORS } from '@/configs/validator';
import { getLocalStorageItem } from '@/helpers/storage';
import { syncNewUserStorageData } from '@/helpers/utils';
import IcoCamera from '@/assets/svg/ico_camera.svg?react';
import IcoGallery from '@/assets/svg/ico_gallery.svg?react';
import InfoNotice from '@/components/blocks/InfoNotice/InfoNotice.component';
import Button from '@/components/controls/Button/Button.component';
import GallerySourceForm from '@/components/forms/GallerySourceForm/GallerySourceForm.component';
import ModalSheet from '@/components/interactive/ModalSheet/ModalSheet.component';
import WebCamSourceBlock from '@/components/interactive/WebCamSourceBlock/WebCamSourceBlock.component';
import styles from '@/components/widgets/AddPhotoWidget/SourceChooserBottomSheet/SourceChooserBottomSheet.module.scss';
import { SourceChooserBottomSheetPropsIF } from '@/components/widgets/AddPhotoWidget/SourceChooserBottomSheet/SourceChooserBottomSheet.types';
import { STORAGE_KEYS } from '@/constants/storageKeys';

const SourceChooserBottomSheet: FC<SourceChooserBottomSheetPropsIF> = ({
    onClose,
    addFiles,
    isDisabled,
    openPhotoAdviceModal,
    openMakePhotoBetterModal,
    openFullTipsBottomSheet,
}) => {
    const [onboardingData, setOnboardingData] =
        useState<NewUserStorageDataIF | null>(
            getLocalStorageItem<NewUserStorageDataIF>(
                STORAGE_KEYS.NewUserFlags,
            ) || null,
        );

    const {
        isShowGallerySourceFormOnboarding,
        isShowCameraSourceFormOnboarding,
        isShowMaxFilesCountInfo,
    } = onboardingData || {};

    const closeHandler = (): void => {
        setOnboardingData(() =>
            syncNewUserStorageData({
                isShowMaxFilesCountInfo: false,
            }),
        );

        onClose();
    };

    const showOnboarding = (type: 'gallery' | 'camera'): void => {
        if (type === 'gallery') {
            openPhotoAdviceModal();
            setOnboardingData(() =>
                syncNewUserStorageData({
                    isShowGallerySourceFormOnboarding: false,
                }),
            );
        }

        if (type === 'camera') {
            openMakePhotoBetterModal();
            setOnboardingData(() =>
                syncNewUserStorageData({
                    isShowCameraSourceFormOnboarding: false,
                }),
            );
        }
    };

    return (
        <ModalSheet
            classNameBody="flcol flc gapBlock"
            onClose={closeHandler}
            bottomSheetMobile
            dataTest="source_chooser_modal_sheet"
        >
            <h2 className={styles.label}>
                Добавить новые
                <br />
                изделия в гардероб
            </h2>
            <div className={styles.chooseImageSourceBlock}>
                <div className={styles.buttonWrapper}>
                    <WebCamSourceBlock
                        className={`${styles.button} ${styles.light}`}
                        addFiles={addFiles}
                        isDisabled={isDisabled}
                    >
                        <IcoCamera />
                        <span>Камера</span>
                    </WebCamSourceBlock>
                    {!!isShowCameraSourceFormOnboarding && (
                        <Button
                            className={styles.showOnboardingButton}
                            isCustom
                            dataTest="show_camera_source_form_onboarding_button"
                            clickHandler={() => showOnboarding('camera')}
                        />
                    )}
                </div>
                <div className={styles.buttonWrapper}>
                    <GallerySourceForm
                        className={styles.button}
                        addFiles={addFiles}
                        isDisabled={isDisabled}
                        isMulti
                    >
                        <IcoGallery />
                        <span>Галерея</span>
                    </GallerySourceForm>
                    {!!isShowGallerySourceFormOnboarding && (
                        <Button
                            className={styles.showOnboardingButton}
                            isCustom
                            dataTest="show_gallery_source_form_onboarding_button"
                            clickHandler={() => showOnboarding('gallery')}
                        />
                    )}
                </div>
            </div>
            {!!isShowMaxFilesCountInfo && (
                <InfoNotice>
                    Одновременно можно загрузить до{' '}
                    {FILE_VALIDATORS.image.maxFilesCount} предметов
                </InfoNotice>
            )}
            <Button
                className={styles.photoAdvice}
                clickHandler={openFullTipsBottomSheet}
                isCustom
                dataTest="open_full_tips_bottom_sheet_button"
            >
                Как сделать фото лучше
            </Button>
        </ModalSheet>
    );
};

export default SourceChooserBottomSheet;
